













































































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { AppModule } from '@/store/modules/app';
import {
  formatDate,
  stringToIDate,
  formatApiError,
  stringFromIDate,
  translateViewResource,
  compareString,
} from '@/utils/index';
import { translateRouteTitle } from '@/utils/permission';
import { list_agent_summaries, listAgentSummaries } from '@/api/agent';
import { send_message, sendMessage } from '@/api/message';
import { AgentTypeEnum, CountryEnum } from '@/api/types';
import { UserModule } from '@/store/modules/user';
import { AppRoleType } from '@/utils/types';

type ItemType = {
  text: string;
  value: string;
  isPastInput?: boolean;
};

@Component({
  name: 'MessageCreate',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  private valid = true;
  private loading = true;
  private allAgentCountries: {
    [country: string]: string[];
  } = {};
  private countries: CountryEnum[] = [];
  private countryItems: ItemType[] = [];
  private agentIds: string[] = [];
  private allAgentItems: { [agentId: string]: ItemType } = {};
  private agentItems: ItemType[] = [];
  private agentsRules: ((v: string[]) => boolean | string)[] = [];
  private subject = '';
  private subjectRules: ((v: string) => boolean | string)[] = [];
  private body = '';
  private bodyRules: ((v: string) => boolean | string)[] = [];
  private redirectAfterCreate = true;
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get appLanguage() {
    return AppModule.language;
  }

  get canShowAgent() {
    return UserModule.roles.some(r => (r as AppRoleType) !== AppRoleType.AGENT);
  }

  get canSubmit() {
    return this.valid;
  }

  @Watch('appLanguage')
  private onLangSelected() {
    (this.$refs.form as any).resetValidation();
  }

  created() {
    this.agentsRules = [
      (v: string[]) =>
        (!!v && v.length > 0) ||
        (this.$t('{msg} is required', {
          msg: this.$t('Agent'),
        }) as string),
    ];
    this.subjectRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Subject'),
        }) as string),
    ];
    this.bodyRules = [
      (v: string) =>
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$t('Content'),
        }) as string),
    ];
  }

  mounted() {
    this.featchData();
  }

  private async featchData() {
    await this.loadAllAgentItems();
    this.loadCountryItems();
    this.loadAgentItemsByCountries(this.countries);
  }

  private async loadAllAgentItems() {
    if (this.canShowAgent) {
      try {
        const listAgentsRes = await listAgentSummaries({
          agentTypes: Object.values(AgentTypeEnum).filter(
            t => (t as AgentTypeEnum) !== AgentTypeEnum.UNKNOWN_AGENT_TYPE
          ),
        } as list_agent_summaries.Request);
        const allItems: { [agentId: string]: ItemType } = {};
        listAgentsRes.agentSummaries.forEach(a => {
          allItems[a.id] = {
            text: a.name,
            value: a.id,
          };
          if (!this.allAgentCountries[a.country]) {
            this.allAgentCountries[a.country] = [];
          }
          this.allAgentCountries[a.country].push(a.id);
        });
        this.allAgentItems = allItems;
      } catch (err) {
        this.$toast.error(
          this.$t('Failed to {do} due to {err}', {
            do: (this.$t('Fetch {msg}', {
              msg: this.$t('Agent'),
            }) as string).toLowerCase(),
            err: formatApiError(this.$i18n, err),
          }) as string,
          {
            icon: 'mdi-alert',
          }
        );
      }
    }
  }

  private loadCountryItems() {
    if (this.canShowAgent) {
      this.countryItems = Object.values(CountryEnum)
        .map(c => ({
          text: `${this.$t('CountryEnum.' + c)}(${c})`,
          value: c,
        }))
        .sort((c1, c2) =>
          this.allAgentCountries[c1.value]
            ? this.allAgentCountries[c2.value]
              ? compareString(c1.value, c2.value)
              : -1
            : this.allAgentCountries[c2.value]
            ? 1
            : compareString(c1.value, c2.value)
        ) as ItemType[];
    }
  }

  private loadAgentItemsByCountries(countries: CountryEnum[]) {
    const agentItems: ItemType[] = [];
    if (countries && countries.length > 0) {
      countries.forEach(c => {
        (this.allAgentCountries[c] || []).forEach(id => {
          agentItems.push(this.allAgentItems[id]);
        });
      });
    } else {
      (Object.values(this.allAgentCountries) || []).forEach(ids =>
        ids.forEach(id => agentItems.push(this.allAgentItems[id]))
      );
    }
    this.agentItems = agentItems;
  }

  private onCountriesChanged(countries: CountryEnum[]) {
    this.loadAgentItemsByCountries(countries);

    if (countries.length === 0) {
      this.agentIds = [];
    } else {
      let agentIds: string[] = [];
      countries.forEach(c => {
        if (this.allAgentCountries[c]) {
          agentIds = agentIds.concat(this.allAgentCountries[c]);
        }
      });
      this.agentIds = agentIds;
    }
  }

  private async submit() {
    AppModule.StartProgress();
    try {
      const req = {
        subject: this.subject,
        body: this.body,
      } as send_message.Request;
      if (this.canShowAgent) {
        req.agentIds = this.agentIds;
      }
      const res = await sendMessage(req);

      this.postSubmit(req, res);
      AppModule.StopProgress();
      this.loading = false;
    } catch (err) {
      AppModule.StopProgress();
      this.loading = false;
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.translateRouteTitle(
            this.$route.meta.title
          ) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private postSubmit(req: send_message.Request, res: send_message.Response) {
    this.reset();
    this.$toast.success(
      this.$t('Succeeded {do}', {
        do: (this.translateRouteTitle(
          this.$route.meta.title
        ) as string).toLowerCase(),
      }) as string,
      {
        icon: this.$route.meta.icon,
      }
    );
  }

  private reset() {
    (this.$refs.form as any).reset();
  }

  private validate(): boolean {
    return (this.$refs.form as any).validate();
  }

  private resetValidation() {
    (this.$refs.form as any).resetValidation();
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForSubmit() {
    this.dialogTextTitle = `${this.translateRouteTitle(
      this.$route.meta.title
    )}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.submit();
    };
    this.dialog = true;
  }

  private translateViewResource(
    resource: string,
    options?: { [key: string]: any }
  ) {
    return translateViewResource(
      this.$i18n,
      this.$options.name as string,
      resource,
      options
    );
  }

  private translateRouteTitle(title: string) {
    return translateRouteTitle(this.$i18n, title);
  }
}
